/**
 * Token storage strategy:
 *   - Access token: in-memory only. Lost on tab refresh; we re-fetch on
 *     mount via the refresh token.
 *   - Refresh token: localStorage. Rotated on every use. Survives tab
 *     close + restart.
 *
 * Trade-off: refresh token in localStorage is XSS-readable. Acceptable
 * for a v1 SaaS dashboard; revisit if pursuing SOC 2 (move to HttpOnly
 * cookie).
 */

const REFRESH_KEY = 'breaker:refresh';

let accessToken: string | null = null;
let accessTokenExpiresAt: number | null = null;
/**
 * Set to true when a refresh attempt fails (server rejected the refresh
 * token) — distinct from "user signed out". The login page reads this to
 * decide whether to show "your session expired" vs. nothing.
 */
let sessionExpired = false;

const subscribers = new Set<() => void>();
function notify() {
  for (const cb of subscribers) cb();
}

export const tokenStore = {
  getAccess(): string | null {
    return accessToken;
  },

  getAccessExpiresAt(): number | null {
    return accessTokenExpiresAt;
  },

  /** True if the access token exists and isn't within 30s of expiring. */
  hasValidAccess(): boolean {
    if (!accessToken || !accessTokenExpiresAt) return false;
    return accessTokenExpiresAt - Date.now() > 30_000;
  },

  setAccess(token: string | null, expiresAtIso: string | null): void {
    accessToken = token;
    accessTokenExpiresAt = expiresAtIso ? new Date(expiresAtIso).getTime() : null;
    notify();
  },

  getRefresh(): string | null {
    try {
      return localStorage.getItem(REFRESH_KEY);
    } catch {
      return null;
    }
  },

  setRefresh(token: string | null): void {
    try {
      if (token === null) localStorage.removeItem(REFRESH_KEY);
      else localStorage.setItem(REFRESH_KEY, token);
    } catch {
      // localStorage may be disabled (private mode etc); silent fail.
    }
    notify();
  },

  clear(): void {
    accessToken = null;
    accessTokenExpiresAt = null;
    this.setRefresh(null);
    notify();
  },

  /**
   * Mark the session as expired (server rejected refresh). Distinct from
   * clear() so the login page can show an "expired" notice. Consumed once.
   */
  markExpired(): void {
    sessionExpired = true;
    accessToken = null;
    accessTokenExpiresAt = null;
    this.setRefresh(null);
    notify();
  },

  /** True if the last sign-out happened because of refresh-rejection. */
  consumeExpiredFlag(): boolean {
    if (!sessionExpired) return false;
    sessionExpired = false;
    return true;
  },

  /** Subscribe to changes — used by the React hook to re-render. */
  subscribe(cb: () => void): () => void {
    subscribers.add(cb);
    return () => subscribers.delete(cb);
  },

  isAuthenticated(): boolean {
    return Boolean(accessToken || this.getRefresh());
  },
};
